import { motion } from "@/lib/motion";
import { Database, Bot, Users, Globe, Zap } from "lucide-react";
import { Link } from "react-router-dom";

const services = [
  {
    icon: Database,
    title: "Consumer Data",
    description: "5.5 million UK Consumer records with various criteria. Our custom-built database engine completes counts and exports in record time.",
    highlight: "5.5M+ Records",
    href: "/data"
  },
  {
    icon: Bot,
    title: "AI Chatbots",
    description: "Powered by the latest GPT releases. Natural conversations, not 'botty'. An extension of your team that doesn't need coffee breaks.",
    highlight: "24/7 Active",
    href: "/ai"
  },
  {
    icon: Zap,
    title: "Automations",
    description: "Intelligent workflows that handle the repetitive stuff. Email sequences, CRM syncs, and multi-step processes that run while you sleep.",
    highlight: "Save 10+ Hours",
    href: "/automations"
  },
  {
    icon: Globe,
    title: "Websites",
    description: "Beautiful, high-converting websites built to perform. From landing pages to full e-commerce, we create digital experiences that deliver.",
    highlight: "Custom Built",
    href: "/website-build"
  },
  {
    icon: Users,
    title: "Consultancy",
    description: "We handle your data buying with full transparency, building on solid metrics that benefit you. Multiple sources, one point of contact.",
    highlight: "Full Transparency",
    href: "/consultancy"
  }
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 }
  }
};

export const Services = () => {
  return (
    <section id="data" className="py-24 relative">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-primary text-sm font-semibold tracking-wider uppercase">
            What We Do
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mt-4 mb-6">
            Services that <span className="text-gradient">actually deliver</span>
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg mb-4">
            We're here to take part, not take over. We never want to be your only supplier—just a reliable part of your marketing mix.
          </p>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Our services are designed to plug into your existing setup, not replace it. We work alongside your current tools, teams and suppliers to strengthen what is already there, remove friction and improve performance without unnecessary disruption.
          </p>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {services.map((service, index) => (
            <Link key={service.title} to={service.href}>
              <motion.div
                variants={itemVariants}
                whileHover={{ 
                  y: -12,
                  scale: 1.02,
                  transition: { duration: 0.3, ease: "easeOut" } 
                }}
                className="group relative p-8 rounded-2xl glass hover:border-primary/50 hover:shadow-lg hover:shadow-primary/10 transition-all duration-300 cursor-pointer h-full overflow-hidden"
              >
                {/* Animated gradient background on hover */}
                <motion.div 
                  className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent"
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileHover={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4 }}
                />
                
                {/* Shine effect */}
                <motion.div
                  className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100"
                  style={{
                    background: "linear-gradient(105deg, transparent 40%, hsl(var(--primary) / 0.1) 45%, hsl(var(--primary) / 0.2) 50%, hsl(var(--primary) / 0.1) 55%, transparent 60%)",
                  }}
                  initial={{ x: "-100%" }}
                  whileHover={{ x: "100%" }}
                  transition={{ duration: 0.8, ease: "easeInOut" }}
                />
                
                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-6">
                    <motion.div 
                      className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors"
                      whileHover={{ rotate: [0, -10, 10, -5, 5, 0], scale: 1.15 }}
                      transition={{ duration: 0.6, ease: "easeInOut" }}
                    >
                      <motion.div
                        animate={{ 
                          scale: [1, 1.1, 1],
                        }}
                        transition={{ 
                          duration: 2,
                          repeat: Infinity,
                          delay: index * 0.2,
                          ease: "easeInOut"
                        }}
                      >
                        <service.icon className="w-7 h-7 text-primary" />
                      </motion.div>
                    </motion.div>
                    <motion.span 
                      className="text-xs font-semibold text-primary bg-primary/10 px-3 py-1 rounded-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors"
                      whileHover={{ scale: 1.1 }}
                      transition={{ type: "spring", stiffness: 400 }}
                    >
                      {service.highlight}
                    </motion.span>
                  </div>
                  
                  <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors duration-300">
                    {service.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed group-hover:text-foreground/80 transition-colors duration-300">
                    {service.description}
                  </p>
                  
                  {/* Arrow indicator */}
                  <motion.div
                    className="mt-4 flex items-center text-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    initial={{ x: -10 }}
                    whileHover={{ x: 0 }}
                  >
                    <span className="text-sm font-medium">Learn more</span>
                    <motion.span
                      className="ml-2"
                      animate={{ x: [0, 4, 0] }}
                      transition={{ duration: 1, repeat: Infinity, ease: "easeInOut" }}
                    >
                      →
                    </motion.span>
                  </motion.div>
                </div>
              </motion.div>
            </Link>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
